import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import type { DealSearchQueryInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-search-query.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { StatusLabelComponent } from '@zaoblako/web/shared/components/status-label/status-label.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { TruncatePipe } from '@zaoblako/web/shared/pipes/truncate.pipe';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';

import { isNil } from 'lodash';
import { map } from 'rxjs/operators';

interface DealMultiSelectOptionInterface extends DealFindInterface {
  dealCode?: string;
  /** Совпадает с текстом фильтра — чтобы PrimeNG не отсекал ответ API повторной клиентской фильтрацией. */
  serverFilterPass?: string;
}

@Component({
  selector: 'crm-deal-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent, StatusLabelComponent, SpinnerComponent, TruncatePipe, SafeHtmlPipe],
  templateUrl: './deal-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealMultiSelectComponent implements OnInit {
  private static readonly ENTITY_CACHE_KIND = 'deal';

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @ViewChild('multiSelectComponent', { static: true }) public multiSelectComponent: MultiSelectComponent;

  @Input() public deals: DealMultiSelectOptionInterface[] = [];
  public queryParams: DealSearchQueryInterface = {
    limit: 50,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;
  /** True while selected IDs from parent are being resolved into entities. */
  public selectionLoading = false;

  private activeSearchQuery = '';

  public _selectedDeals: DealMultiSelectOptionInterface[] = [];
  @Input() public set selectedDeals(selectedDeals: DealFindInterface[]) {
    if (!this.customSearch) {
      if (!selectedDeals?.every((selectedDeal) => this.deals.some((deal) => deal.id === selectedDeal.id))) {
        const ids = selectedDeals
          ?.filter((selectedDeal) => !this.deals.some((deal) => deal.id === selectedDeal.id))
          .map((selectedDeal) => selectedDeal.id);
        if (ids?.length) {
          ids.forEach((id, index) => {
            if (typeof id === 'string') {
              ids[index] = +id;
            }
          });
        }
        if (ids?.length) {
          this.entityCache
            .loadByIds(DealMultiSelectComponent.ENTITY_CACHE_KIND, ids, (missing) =>
              this.dealService
                .flatList({
                  ids: missing as number[],
                  limit: missing.length > 500 ? 500 : missing.length,
                  offset: 0
                })
                .pipe(map((data) => data.items))
            )
            .subscribe({
              next: (items) => {
                this.appendDeals(items);
                this.cdRef.markForCheck();
                setTimeout(() => {
                  this._selectedDeals = this.deals.filter((deal) => ids.includes(deal.id));
                  this.cdRef.markForCheck();
                }, 30);
              }
            });
        }
      }
    } else {
      this._selectedDeals = this.enrichDealOptions(selectedDeals ?? []);
    }
  }

  @Output() public selectedDealsChange = new EventEmitter<DealFindInterface[]>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = false;
  @Input() public label: string;
  @Input() public placeholder = '\u00A0';
  @Input() public isShowStatus = true;
  @Input() public itemStyleClass: string;
  @Input() public panelStyleClass = 'crm-deal-multi-select-panel';
  @Input() public panelStyle: Record<string, string> = {
    minWidth: '28rem',
    maxWidth: 'min(36rem, 96vw)'
  };

  @Input() public customSearch = false;

  @Input() public set setSelectedDealsIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }
    if (
      ids?.length &&
      ((this._selectedDeals?.length && !this._selectedDeals?.every((selectedDeal) => ids.includes(selectedDeal.id))) ||
        !this._selectedDeals?.length)
    ) {
      this.selectionLoading = true;
      this.cdRef.markForCheck();
      let newIds = ids.filter((id) => !this.deals.some((deal) => deal.id === id));
      if (newIds?.length) {
        this.entityCache
          .loadByIds(DealMultiSelectComponent.ENTITY_CACHE_KIND, newIds, (missing) =>
            this.dealService
              .flatList({
                ids: missing as number[],
                limit: missing.length > 500 ? 500 : missing.length,
                offset: 0
              })
              .pipe(map((data) => data.items))
          )
          .subscribe({
            next: (items) => {
              this.appendDeals(items);
              this.cdRef.markForCheck();
              setTimeout(() => {
                this.applySelectionFromRequestedIds(ids);
              }, 30);
            },
            error: () => {
              this.selectionLoading = false;
              this.onSelect([]);
              this.cdRef.markForCheck();
            }
          });
      } else {
        this.applySelectionFromRequestedIds(ids);
      }
    } else if (!ids?.length) {
      this.selectionLoading = false;
    }
  }

  public constructor(
    private readonly dealService: DealService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public ngOnInit() {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      const fingerprint = this.entityCache.fingerprint({ ...this.queryParams });
      this.entityCache
        .loadFirstPage(DealMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
          this.dealService.search(this.queryParams)
        )
        .subscribe({
          next: (data) => {
            if (this.deals?.length) {
              this.appendDeals(data.items);
            } else {
              this.deals = this.enrichDealOptions(data.items);
            }
            this.count = data.count;
            this.loading = false;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loading = false;
            this.cdRef.markForCheck();
          }
        });
    }
  }

  public toDeal(deal: any): DealFindInterface {
    return deal as DealFindInterface;
  }

  public onSelect(deals: DealFindInterface[]) {
    this.entityCache.putEntities(DealMultiSelectComponent.ENTITY_CACHE_KIND, deals ?? []);
    this.selectedDealsChange.emit(deals);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.multiSelectComponent?.multiSelect?.scroller.onScroll.subscribe(() => {
          let el: HTMLElement = this.multiSelectComponent?.multiSelect?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.deals.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.dealService.search(this.queryParams).subscribe({
                next: (data) => {
                  this.appendDeals(data.items);
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public searchDeal(name: string) {
    if (!this.customSearch) {
      this.activeSearchQuery = (name ?? '').trim();
      this.loading = true;
      this.queryParams.search = this.activeSearchQuery.length > 0 ? this.activeSearchQuery : undefined;
      this.queryParams.offset = 0;
      this.deals = this.enrichDealOptions([...(this._selectedDeals ?? [])], this.activeSearchQuery);
      this.cdRef.markForCheck();

      this.dealService.search(this.queryParams).subscribe({
        next: (data) => {
          const pinnedSelected = this._selectedDeals?.filter(
            (selectedDeal) => !data.items.some((deal) => deal.id === selectedDeal.id)
          );
          this.deals = this.enrichDealOptions([...data.items, ...(pinnedSelected ?? [])], this.activeSearchQuery);
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.multiSelectComponent?.multiSelect?.filterValue);
  }

  private enrichDealOption = (
    deal: DealFindInterface,
    searchQuery?: string
  ): DealMultiSelectOptionInterface => {
    const pass = searchQuery ?? this.activeSearchQuery;
    return {
      ...deal,
      dealCode: !isNil(deal.number) ? `ФРМ-${deal.number}` : undefined,
      serverFilterPass: pass
    };
  };

  private enrichDealOptions = (
    deals: DealFindInterface[],
    searchQuery?: string
  ): DealMultiSelectOptionInterface[] => (deals ?? []).map((deal) => this.enrichDealOption(deal, searchQuery));

  private appendDeals = (incoming: DealFindInterface[]): void => {
    const newItems = incoming.filter((item) => !this.deals.some((deal) => deal.id === item.id));
    this.deals = this.deals.concat(this.enrichDealOptions(newItems));
  };

  private applySelectionFromRequestedIds(requestedIds: number[]): void {
    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.deals, requestedIds);
    this._selectedDeals = selected;
    this.selectionLoading = false;
    if (hasStaleKeys) {
      this.onSelect(selected);
    }
    this.cdRef.markForCheck();
  }
}
